import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Department { id: string; department_name: string }
type AccessLevel = 'super_admin' | 'operations_manager' | 'staff';
interface Props {
  staffId: string;
  staffName: string;
  initialDepartmentIds: string[];
  initialDepartmentId: string | null;
  initialJobTitle: string | null;
  initialAccessLevel: AccessLevel;
  initialPermissions: string[];
  initialLandingPage: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const permissionOptions = [
  ['manage_shifts', 'シフト作成・公開'],
  ['approve_attendance', '勤怠確認・承認'],
  ['manage_operations', '担当部門の業務管理'],
  ['send_notifications', 'スタッフ通知'],
  ['view_reports', '担当部門のレポート閲覧'],
] as const;

export default function StaffOrganizationModal(props: Props) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>(props.initialDepartmentIds);
  const [primaryDepartmentId, setPrimaryDepartmentId] = useState(props.initialDepartmentId || '');
  const [jobTitle, setJobTitle] = useState(props.initialJobTitle || '');
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(props.initialAccessLevel);
  const [permissions, setPermissions] = useState<string[]>(props.initialPermissions);
  const [landingPage, setLandingPage] = useState(props.initialLandingPage || '');
  const [engagementType,setEngagementType]=useState<'employee'|'contractor'>('employee');
  const [settlementType,setSettlementType]=useState<'monthly_payroll'|'daily_cash'|'daily_transfer'>('monthly_payroll');
  const [accountingEntryType,setAccountingEntryType]=useState<'labor'|'cost_of_sales'|'operating_expense'>('labor');
  const [accountingCategory,setAccountingCategory]=useState('給与・賃金');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('staff_departments').select('id,department_name').eq('is_active', true).order('sort_order')
      .then(({ data }) => setDepartments((data || []) as Department[]));
    supabase.from('staff_engagement_profiles').select('engagement_type,settlement_type,accounting_entry_type,accounting_category').eq('staff_id',props.staffId).maybeSingle().then(({data})=>{if(data){setEngagementType(data.engagement_type as 'employee'|'contractor');setSettlementType(data.settlement_type as 'monthly_payroll'|'daily_cash'|'daily_transfer');setAccountingEntryType(data.accounting_entry_type as 'labor'|'cost_of_sales'|'operating_expense');setAccountingCategory(data.accounting_category)}});
  }, [props.staffId]);

  const toggleDepartment = (id: string) => {
    setDepartmentIds((current) => {
      if (current.includes(id)) {
        if (primaryDepartmentId === id) setPrimaryDepartmentId('');
        return current.filter((value) => value !== id);
      }
      if (!primaryDepartmentId) setPrimaryDepartmentId(id);
      return [...current, id];
    });
  };

  const togglePermission = (key: string) => setPermissions((current) =>
    current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
  );

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (accessLevel !== 'super_admin' && departmentIds.length === 0) {
      setError('運用管理者・一般スタッフには所属部門を1つ以上設定してください');
      return;
    }
    setSaving(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('admin_save_staff_access', {
      p_staff_id: props.staffId,
      p_access_level: accessLevel,
      p_primary_department_id: primaryDepartmentId || null,
      p_department_ids: departmentIds,
      p_job_title: jobTitle.trim() || null,
      p_permissions: accessLevel === 'operations_manager' ? permissions : [],
    });
    const result = data as { success?: boolean } | null;
    if (rpcError || !result?.success) {
      setError('所属・役職・システム権限を保存できませんでした');
      setSaving(false);
      return;
    }
    const { data: landingData, error: landingError } = await supabase.rpc('admin_set_staff_landing_page', {
      p_staff_id: props.staffId,
      p_landing_page: landingPage || null,
    });
    if (landingError || !(landingData as { success?: boolean } | null)?.success) {
      setError('ログイン後の開始画面を保存できませんでした');
      setSaving(false);
      return;
    }
    const {data:engagementData,error:engagementError}=await supabase.rpc('admin_set_staff_engagement',{p_staff_id:props.staffId,p_engagement_type:engagementType,p_settlement_type:settlementType,p_accounting_entry_type:accountingEntryType,p_accounting_category:accountingCategory.trim()});
    if(engagementError||!(engagementData as {success?:boolean}|null)?.success){setError('契約・精算区分を保存できませんでした');setSaving(false);return}
    const approvalEnabled=accessLevel==='operations_manager'&&permissions.includes('approve_attendance');
    const approvalResults=await Promise.all(departments.map(department=>supabase.rpc('admin_set_department_approval_manager',{p_staff_id:props.staffId,p_department_id:department.id,p_enabled:approvalEnabled&&departmentIds.includes(department.id)})));
    if(approvalResults.some(result=>result.error)){setError('所属長の承認範囲を保存できませんでした');setSaving(false);return}
    props.onSuccess();
  };

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
      <div className="flex justify-between mb-5">
        <div><h3 className="font-semibold">所属・役職・システム権限</h3><p className="text-xs text-foreground-600 mt-1">{props.staffName}</p></div>
        <button onClick={props.onClose}><i className="ri-close-line text-xl" /></button>
      </div>
      {error && <div className="mb-4 bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
      <form onSubmit={submit} className="space-y-5">
        <section>
          <label className="block text-sm font-semibold mb-2">所属部門（複数選択可）</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {departments.map((department) => <label key={department.id} className="flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={departmentIds.includes(department.id)} onChange={() => toggleDepartment(department.id)} />
              <span className="text-sm">{department.department_name}</span>
            </label>)}
          </div>
          {departmentIds.length > 0 && <div className="mt-3"><label className="block text-xs text-foreground-600 mb-1">主所属</label><select value={primaryDepartmentId} onChange={(e) => setPrimaryDepartmentId(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50">{departmentIds.map((id) => <option key={id} value={id}>{departments.find((d) => d.id === id)?.department_name}</option>)}</select></div>}
        </section>

        <div><label className="block text-sm font-semibold mb-1">役職</label><input maxLength={100} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="例：役員、マネージャー、社員、スタッフ" className="w-full px-3.5 py-2.5 rounded-lg border border-background-200" /><p className="text-xs text-foreground-500 mt-1">役職は人事上の名称です。操作権限とは別に管理します。</p></div>

        <section>
          <label className="block text-sm font-semibold mb-2">システム権限</label>
          <div className="space-y-2">
            {([
              ['super_admin','最高管理者','全社、給与、契約書、個人書類、権限設定を含む全操作'],
              ['operations_manager','運用管理者','担当部門の日常運用。下の個別権限を追加できます'],
              ['staff','一般スタッフ','自分の勤怠・公開シフトと所属部門の通常業務'],
            ] as const).map(([value,label,description]) => <label key={value} className={`block rounded-lg border p-3 cursor-pointer ${accessLevel === value ? 'border-primary-500 bg-primary-50' : 'border-background-200'}`}><div className="flex gap-2"><input type="radio" name="access" checked={accessLevel === value} onChange={() => setAccessLevel(value)} /><div><p className="text-sm font-semibold">{label}</p><p className="text-xs text-foreground-600 mt-0.5">{description}</p></div></div></label>)}
          </div>
        </section>

        {accessLevel === 'operations_manager' && <section><label className="block text-sm font-semibold mb-2">個別権限</label><div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{permissionOptions.map(([key,label]) => <label key={key} className="flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2.5 cursor-pointer"><input type="checkbox" checked={permissions.includes(key)} onChange={() => togglePermission(key)} /><span className="text-sm">{label}</span></label>)}</div></section>}

        <section className="rounded-xl border border-background-200 p-4 space-y-3"><h4 className="font-semibold">契約・精算区分</h4><div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><label className="text-sm">契約区分<select value={engagementType} onChange={e=>{const contractor=e.target.value==='contractor';setEngagementType(contractor?'contractor':'employee');setAccountingEntryType(contractor?'cost_of_sales':'labor');setAccountingCategory(contractor?'業務委託費':'給与・賃金')}} className="mt-1 w-full px-3 py-2.5 rounded-lg border bg-white"><option value="employee">雇用スタッフ</option><option value="contractor">業務委託</option></select></label><label className="text-sm">精算方法<select value={settlementType} onChange={e=>setSettlementType(e.target.value as typeof settlementType)} className="mt-1 w-full px-3 py-2.5 rounded-lg border bg-white"><option value="monthly_payroll">月次給与・報酬</option><option value="daily_cash">日払い（現金）</option><option value="daily_transfer">日払い（振込）</option></select></label><label className="text-sm">損益の集計先<select value={accountingEntryType} onChange={e=>setAccountingEntryType(e.target.value as typeof accountingEntryType)} className="mt-1 w-full px-3 py-2.5 rounded-lg border bg-white"><option value="labor">人件費</option><option value="cost_of_sales">原価・業務委託費</option><option value="operating_expense">販売管理費</option></select></label><label className="text-sm">勘定表示名<input required value={accountingCategory} onChange={e=>setAccountingCategory(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border" /></label></div></section>

        <section>
          <label className="block text-sm font-semibold mb-1">ログイン後の開始画面</label>
          <select value={landingPage} onChange={(event) => setLandingPage(event.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50">
            <option value="">全社TOP（標準）</option>
            <option value="/casa">Casa Kokoro オペレーション</option>
            <option value="/reservations">予約管理</option>
            <option value="/cleaning">清掃管理</option>
            <option value="/attendance">勤怠管理</option>
            <option value="/availability">休み希望</option>
            <option value="/shifts">シフト</option>
            <option value="/purchase-requests">発注・手配</option>
            <option value="/reimbursements">立替精算</option>
            <option value="/goldys-closing">Goldy’s 日次締め</option>
          </select>
          <p className="text-xs text-foreground-500 mt-1">全社TOPでは、最高管理者は全事業、その他のスタッフは所属事業だけを開けます。</p>
        </section>

        {accessLevel === 'super_admin' && <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">最高管理者は給与・雇用契約・マイナンバー・在留書類を含むすべての情報へアクセスできます。</div>}
        <div className="flex gap-3 pt-2"><button type="button" onClick={props.onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-background-100">キャンセル</button><button disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold disabled:opacity-60">{saving ? '保存中…' : '保存'}</button></div>
      </form>
    </div>
  </div>;
}
